import { FC, useEffect, useMemo, useState } from 'react';
import { OrderCommand } from '../../services/command';
import TimeHelper from '../../utils/time';
import { SCUIContent } from '../../atomic/organisms';
import { Grid, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { SCUICustomDataGrid } from '../../atomic/molecules';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useHistory } from 'react-router-dom';
import { SCUILoadingLinear } from '../../atomic/atoms';
import { useTranslation } from 'react-i18next';
import { SDO } from './storeDeliveryOrders.classes';

const PAGINATION_STORAGE_KEY = 'storeDeliveryOrders.pagination';
const DEFAULT_PAGE_SIZE = 10;
const ALLOWED_PAGE_SIZES = [5, 10, 20, 50, 100];

const readStoredPagination = (): { page: number; pageSize: number } => {
    try {
        const raw = sessionStorage.getItem(PAGINATION_STORAGE_KEY);
        if (!raw) {
            return { page: 0, pageSize: DEFAULT_PAGE_SIZE };
        }

        const parsed = JSON.parse(raw);
        const pageSize = ALLOWED_PAGE_SIZES.includes(parsed.pageSize) ? parsed.pageSize : DEFAULT_PAGE_SIZE;
        const page = typeof parsed.page === 'number' && parsed.page >= 0 ? parsed.page : 0;

        return { page, pageSize };
    } catch {
        return { page: 0, pageSize: DEFAULT_PAGE_SIZE };
    }
};

const writeStoredPagination = (page: number, pageSize: number) => {
    sessionStorage.setItem(PAGINATION_STORAGE_KEY, JSON.stringify({ page, pageSize }));
};

const getCalendarDayDifference = (from: Date, to: Date): number => {
    const startOfFrom = new Date(from);
    startOfFrom.setHours(0, 0, 0, 0);
    const startOfTo = new Date(to);
    startOfTo.setHours(0, 0, 0, 0);
    return Math.floor((startOfTo.getTime() - startOfFrom.getTime()) / (1000 * 60 * 60 * 24));
};

export const StoreDeliveryOrders: FC<any> = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(() => readStoredPagination().page);
    const [pageSize, setPageSize] = useState(() => readStoredPagination().pageSize);

    const storeCodeInfo = localStorage.getItem('storeCode');

    const history = useHistory();
    const { t } = useTranslation();

    const GetStoreOrders = () => {
        if (storeCodeInfo) {
            setLoading(true);

            OrderCommand.StoreDeliveryOrders({ storeCode: storeCodeInfo })
                .then((res: any) => {
                    if (res) {
                        let parseData = res.storeOrderPendingShippingLists.map((item: any) => {
                            return {
                                customerFullName: item.customerFullName.trim().length > 0 ? item.customerFullName : '-',
                                customerId: item.customerId || ' ',
                                orderDate: TimeHelper.formatDateHour(item.orderDate) || ' ',
                                orderId: item.orderId || ' ',
                                orderNumber: item.orderNumber || ' ',
                                shippingListId: item.shippingListId || ' ',
                                shippingListNo: item.shippingListNo || ' ',
                                shippingListStatus: item.shippingListStatus || ' ',
                                shippingListStatusId: item.shippingListStatusId || ' ',
                                storeDeliveryDate: item.storeDeliveryDate,
                                storeCode: storeCodeInfo && storeCodeInfo,
                            };
                        });
                        setOrders(parseData);
                    }
                })
                .catch((error: any) => {
                    console.log('Hata :>> ', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    function handleClick(shippingListNo: any) {
        history.push('/orderDetail?shippingListNo=' + shippingListNo);
    }

    useEffect(() => {
        GetStoreOrders();
    }, []);

    useEffect(() => {
        writeStoredPagination(pageNumber, pageSize);
    }, [pageNumber, pageSize]);

    useEffect(() => {
        if (!orders.length) {
            return;
        }

        const maxPage = Math.max(0, Math.ceil(orders.length / pageSize) - 1);
        if (pageNumber > maxPage) {
            setPageNumber(maxPage);
        }
    }, [orders.length, pageSize, pageNumber]);

    const excelDownloadData = useMemo(() => {
        if (!orders.length) {
            return [];
        }

        const columnMapping: Record<string, string> = {
            storeCode: t('general.storeCode'),
            customerFullName: t('general.name'),
            orderNumber: t('general.orderNo'),
            shippingListNo: t('general.shippingNo'),
            shippingListStatus: t('general.status'),
            orderDate: t('general.orderDate'),
            storeDeliveryDate: t('storeDelivery.storeDeliveryDate'),
        };

        return orders.map((row: any) => {
            const mappedRow: Record<string, string> = {};
            Object.keys(columnMapping).forEach((key) => {
                if (key === 'storeDeliveryDate') {
                    mappedRow[columnMapping[key]] = row.storeDeliveryDate
                        ? TimeHelper.formatDateHour(row.storeDeliveryDate)
                        : ' ';
                } else {
                    mappedRow[columnMapping[key]] = row[key];
                }
            });
            return mappedRow;
        });
    }, [orders, t]);

    return (
        <SCUIContent className={SDO.page}>
            <Grid container direction="row" className={SDO.root}>
                <Grid
                    className={SDO.header}
                    size={{
                        xl: 12,
                        lg: 12,
                        md: 6,
                        xs: 6,
                    }}
                >
                    <h1 className={`headtxt ${SDO.title}`}>{t('storeDelivery.pageTitle')}</h1>
                </Grid>
                <Grid
                    className={SDO.loading}
                    size={{
                        xl: 12,
                        lg: 12,
                        md: 12,
                        xs: 12,
                    }}
                >
                    <SCUILoadingLinear display={loading} />
                </Grid>
                <Grid
                    className={SDO.grid}
                    sx={{
                        '& .overdue-row': {
                            backgroundColor: '#df9797 !important',
                        },
                    }}
                    size={{
                        xl: 12,
                        lg: 12,
                        md: 12,
                        xs: 12,
                    }}
                >
                    <SCUICustomDataGrid
                        randomId
                        rows={orders}
                        page={pageNumber}
                        pageSize={pageSize}
                        onPageChange={setPageNumber}
                        onPageSizeChange={setPageSize}
                        excelDownloadButton={true}
                        excelDownloadData={excelDownloadData}
                        excelDownloadFolderName={'StoreDeliveryOrders'}
                        excelDownloadButtonName={t('general.exportToExcel')}
                        getRowClassName={(params: any) => {
                            if (!params.row.storeDeliveryDate) {
                                return '';
                            }

                            const storeDeliveryDate = new Date(params.row.storeDeliveryDate);
                            if (Number.isNaN(storeDeliveryDate.getTime())) {
                                return '';
                            }

                            const differenceInDays = getCalendarDayDifference(storeDeliveryDate, new Date());
                            return differenceInDays > 5 ? 'overdue-row' : '';
                        }}
                        columns={[
                            {
                                field: 'storeCode',
                                headerName: t('general.storeCode'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => (
                                    <span
                                        className={SDO.cellStoreCode}
                                        data-store-code={params.row.storeCode}
                                    >
                                        {params.row.storeCode}
                                    </span>
                                ),
                            },
                            {
                                field: 'customerFullName',
                                headerName: t('general.name'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => (
                                    <span
                                        className={SDO.cellCustomerName}
                                        data-customer-name={params.row.customerFullName}
                                    >
                                        {params.row.customerFullName}
                                    </span>
                                ),
                            },
                            {
                                field: 'orderNumber',
                                headerName: t('general.orderNo'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => (
                                    <span
                                        className={SDO.cellOrderNumber}
                                        data-order-number={params.row.orderNumber}
                                    >
                                        {params.row.orderNumber}
                                    </span>
                                ),
                            },
                            {
                                field: 'shippingListNo',
                                headerName: t('general.shippingNo'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => {
                                    return (
                                        <a
                                            className={`linktxt ${SDO.cellShippingNo} ${SDO.cellShippingNoLink}`}
                                            data-shipping-list-no={params.row.shippingListNo}
                                            onClick={() => handleClick(params.row.shippingListNo)}
                                            rel="noopener noreferrer"
                                        >
                                            {params?.row.shippingListNo}
                                        </a>
                                    );
                                },
                            },
                            {
                                field: 'shippingListStatus',
                                headerName: t('general.status'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => (
                                    <span
                                        className={SDO.cellShippingStatus}
                                        data-status={params.row.shippingListStatus}
                                        data-status-id={params.row.shippingListStatusId}
                                    >
                                        {params.row.shippingListStatus}
                                    </span>
                                ),
                            },
                            {
                                field: 'orderDate',
                                headerName: t('general.orderDate'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => (
                                    <span
                                        className={SDO.cellOrderDate}
                                        data-order-date={params.row.orderDate}
                                    >
                                        {params.row.orderDate}
                                    </span>
                                ),
                            },
                            {
                                field: 'storeDeliveryDate',
                                headerName: t('storeDelivery.storeDeliveryDate'),
                                width: 150,
                                flex: 1,
                                renderCell: (params: any) => {
                                    const formatted = TimeHelper.formatDateHour(params.value);
                                    return (
                                        <Typography
                                            className={SDO.cellDeliveryDate}
                                            data-delivery-date={params.value}
                                        >
                                            {formatted}
                                        </Typography>
                                    );
                                },
                            },
                            {
                                field: 'deneme ',
                                headerName: ' ',
                                align: 'center',
                                headerAlign: 'center',
                                flex: 1,
                                renderCell: (params: any) => {
                                    const handleClick = (popupState: any, tur: string) => {
                                        popupState.close();
                                        switch (tur) {
                                            case 'gotoDetail': {
                                                history.push('/orderDetail?OrderNumber=' + params?.row.orderNumber);
                                                break;
                                            }
                                            case 'return': {
                                                history.push('/createReturn/?orderNumber=' + params.row.orderNumber);
                                                break;
                                            }
                                            default: {
                                                break;
                                            }
                                        }
                                    };
                                    return (
                                        <PopupState variant="popover" popupId="demo-popup-menu">
                                            {(popupState: any) => (
                                                <div className={SDO.cellActions}>
                                                    <IconButton
                                                        {...bindTrigger(popupState)}
                                                        className={SDO.btnRowMenu}
                                                        data-order-number={params.row.orderNumber}
                                                    >
                                                        {' '}
                                                        <MoreVertIcon className="svg_icons" />{' '}
                                                    </IconButton>
                                                    <Menu {...bindMenu(popupState)} className={SDO.rowMenu}>
                                                        <MenuItem
                                                            onClick={() => handleClick(popupState, 'return')}
                                                            className={`${SDO.rowMenuItem} ${SDO.rowMenuItemReturn}`}
                                                            data-action="return"
                                                        >
                                                            {t('refund.createRefund')}{' '}
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => handleClick(popupState, 'gotoDetail')}
                                                            className={`${SDO.rowMenuItem} ${SDO.rowMenuItemDetail}`}
                                                            data-action="goto-detail"
                                                        >
                                                            {t('general.goToDetail')}{' '}
                                                        </MenuItem>
                                                    </Menu>
                                                </div>
                                            )}
                                        </PopupState>
                                    );
                                },
                            },
                        ]}
                    />
                </Grid>
            </Grid>
        </SCUIContent>
    );
};
